/* ────────────────────────────────────────────────────────────
   Divine Designs · collection.js

   Powers the collection page UX defined in
   _foundation/collection_strategy.md and rendered by
   sections/main-collection.liquid.

   Behaviors:
     - Auto-apply filters on input change (debounced)
     - Mobile bottom-sheet drawer (toggle, scrim, body lock)
     - Filter group accordion expand/collapse
     - "Show all N" expand for filter lists > 6 values
     - Sort change preserves all current filter URL params
     - Active filter count update on every change
     - Section Rendering API: refetch grid + chips without full reload
     - Pushes URL state via history.pushState

   All behaviors are progressive: noscript form has a hidden
   submit button, so users without JS still get filters.
   ──────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var DEBOUNCE_MS = 350;
  var SECTION_ID_FALLBACK = 'main-collection';

  function $(s, c) { return (c || document).querySelector(s); }
  function $$(s, c) { return Array.from((c || document).querySelectorAll(s)); }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  // ── URL builder ────────────────────────────────────────────
  // Collect current form state + sort, return query string.
  function buildQuery(form, sortValue) {
    var fd = new FormData(form);
    var params = new URLSearchParams();

    // Walk form data; skip empty values
    fd.forEach(function (value, key) {
      if (value !== '' && value !== null) {
        params.append(key, value);
      }
    });

    if (sortValue) params.set('sort_by', sortValue);
    return params.toString();
  }

  // ── Section Rendering API: refetch + swap grid + chips + count ──
  function refresh(form, opts) {
    opts = opts || {};
    var grid = $('[data-collection-results]');
    if (!grid) return;

    var url = form.getAttribute('action') || window.location.pathname;
    var query = buildQuery(form, opts.sort);
    var fullUrl = url + (query ? '?' + query : '');

    grid.setAttribute('data-loading', 'true');

    // Push URL state immediately for back-button support
    if (history && history.pushState) {
      history.pushState({ collectionFilter: true }, '', fullUrl);
    }

    fetch(fullUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        // Swap grid
        var freshGrid = doc.querySelector('[data-collection-results]');
        if (freshGrid) grid.innerHTML = freshGrid.innerHTML;

        // Swap applied-filter chips zone
        var chipsZone = $('[data-collection-applied]');
        var freshChips = doc.querySelector('[data-collection-applied]');
        if (freshChips) {
          if (chipsZone) chipsZone.replaceWith(freshChips);
          else $('[data-collection-utility]').after(freshChips);
        } else if (chipsZone) {
          chipsZone.remove();
        }

        // Swap result count
        var countEl = $('[data-collection-utility] .collection-utility__count');
        var freshCount = doc.querySelector('[data-collection-utility] .collection-utility__count');
        if (countEl && freshCount) countEl.innerHTML = freshCount.innerHTML;

        // Swap active filter count badge
        var badgeEl = $('[data-collection-active-count]');
        var freshBadge = doc.querySelector('[data-collection-active-count]');
        if (badgeEl && freshBadge) {
          badgeEl.innerHTML = freshBadge.innerHTML;
          if (freshBadge.hasAttribute('hidden')) badgeEl.setAttribute('hidden', '');
          else badgeEl.removeAttribute('hidden');
        }

        // Swap mobile CTA result count
        var ctaBtn = $('[data-collection-mobile-cta] [data-collection-filter-close]');
        var freshCtaBtn = doc.querySelector('[data-collection-mobile-cta] [data-collection-filter-close]');
        if (ctaBtn && freshCtaBtn) ctaBtn.innerHTML = freshCtaBtn.innerHTML;

        grid.setAttribute('data-loading', 'false');

        // Scroll to top of results on filter change (not on initial load)
        if (opts.scroll !== false) {
          var utility = $('[data-collection-utility]');
          if (utility) utility.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      })
      .catch(function () {
        grid.setAttribute('data-loading', 'false');
        // Fall back to full submit
        form.submit();
      });
  }

  // ── Filter form: auto-apply on input change ────────────────
  function bindFormAutoApply() {
    var form = $('[data-collection-form]');
    if (!form) return;

    var debouncedRefresh = debounce(function () {
      refresh(form);
    }, DEBOUNCE_MS);

    form.addEventListener('change', function (e) {
      if (e.target.matches('[data-filter-input]')) {
        debouncedRefresh();
      }
    });

    form.addEventListener('input', function (e) {
      // Number inputs (price) — debounce more aggressively
      if (e.target.matches('input[type="number"][data-filter-input]')) {
        debouncedRefresh();
      }
    });

    // Block standard submit (we handle it via fetch); only fall through if JS broken
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      refresh(form);
    });
  }

  // ── Sort dropdown: preserve current filter params ──────────
  function bindSort() {
    document.addEventListener('change', function (e) {
      var sortEl = e.target.closest('[data-collection-sort]');
      if (!sortEl) return;
      var form = $('[data-collection-form]');
      if (!form) return;
      refresh(form, { sort: sortEl.value });
    });
  }

  // ── Filter group accordion ────────────────────────────────
  function bindAccordion() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-filter-toggle]');
      if (!btn) return;
      e.preventDefault();
      var isOpen = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!isOpen));
    });
  }

  // ── Show all N / Show less ─────────────────────────────────
  function bindShowMore() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-filter-show-more]');
      if (!btn) return;
      e.preventDefault();
      var list = btn.parentElement.querySelector('.filter-list');
      if (!list) return;
      var expanded = list.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', String(expanded));
      var labelEl = btn.querySelector('[data-filter-more-label]');
      if (labelEl) {
        if (expanded) {
          labelEl.textContent = 'Show less';
        } else {
          var count = list.dataset.filterValuesCount || '';
          labelEl.textContent = count ? 'Show all ' + count : 'Show all';
        }
      }
    });
  }

  // ── Mobile drawer ──────────────────────────────────────────
  function bindMobileDrawer() {
    var panel = $('[data-collection-filters]');
    var scrim = $('[data-collection-scrim]');
    if (!panel || !scrim) return;

    function open() {
      panel.classList.add('is-open');
      scrim.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      var toggle = $('[data-collection-filter-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }
    function close() {
      panel.classList.remove('is-open');
      scrim.setAttribute('hidden', '');
      document.body.style.overflow = '';
      var toggle = $('[data-collection-filter-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-collection-filter-toggle]')) {
        e.preventDefault();
        if (panel.classList.contains('is-open')) close();
        else open();
        return;
      }
      if (e.target.closest('[data-collection-filter-close]')) {
        e.preventDefault();
        close();
        return;
      }
      if (e.target.closest('[data-collection-scrim]')) {
        close();
      }
    });

    // Esc to close
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) close();
    });
  }

  // ── Applied chip removal: handled by native href on the chip,
  //    but if JS is on, intercept and use the section-render path
  //    so we don't full-reload.
  function bindChipRemoval() {
    document.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-collection-chip-remove], [data-collection-clear-all]');
      if (!chip) return;
      var href = chip.getAttribute('href');
      if (!href) return;
      e.preventDefault();

      var form = $('[data-collection-form]');
      if (!form) {
        window.location.href = href;
        return;
      }

      // Update form to reflect the removed filter, then refresh
      var url = new URL(href, window.location.origin);
      var params = url.searchParams;

      // Reset all filter inputs
      $$('[data-filter-input]', form).forEach(function (input) {
        if (input.type === 'checkbox') input.checked = false;
        else input.value = '';
      });

      // Re-apply remaining params from the removal URL
      params.forEach(function (value, key) {
        if (key === 'sort_by') return;
        var input = form.querySelector('[name="' + CSS.escape(key) + '"][value="' + CSS.escape(value) + '"]');
        if (input && input.type === 'checkbox') {
          input.checked = true;
        } else {
          var anyInput = form.querySelector('[name="' + CSS.escape(key) + '"]');
          if (anyInput) anyInput.value = value;
        }
      });

      refresh(form);
    });
  }

  // ── Browser back/forward — soft refresh, no scroll jump (Baymard G#202) ──
  function syncFormToUrl(form, url) {
    var params = new URL(url, window.location.origin).searchParams;

    // Reset all inputs
    $$('[data-filter-input]', form).forEach(function (input) {
      if (input.type === 'checkbox') input.checked = false;
      else if (input.type === 'number') input.value = '';
    });

    // Re-apply from URL
    params.forEach(function (value, key) {
      if (key === 'sort_by') {
        var sortEl = $('[data-collection-sort]');
        if (sortEl) sortEl.value = value;
        return;
      }
      var checkbox = form.querySelector(
        '[name="' + CSS.escape(key) + '"][value="' + CSS.escape(value) + '"]'
      );
      if (checkbox && checkbox.type === 'checkbox') {
        checkbox.checked = true;
      } else {
        var anyInput = form.querySelector('[name="' + CSS.escape(key) + '"]');
        if (anyInput) anyInput.value = value;
      }
    });
  }

  function bindPopState() {
    window.addEventListener('popstate', function () {
      var form = $('[data-collection-form]');
      if (!form) { window.location.reload(); return; }
      syncFormToUrl(form, window.location.href);
      var sortEl = $('[data-collection-sort]');
      refresh(form, { sort: sortEl ? sortEl.value : null, scroll: false });
    });
  }

  // ── Init ──────────────────────────────────────────────────
  function init() {
    if (!$('[data-collection-form]')) return;
    bindFormAutoApply();
    bindSort();
    bindAccordion();
    bindShowMore();
    bindMobileDrawer();
    bindChipRemoval();
    bindPopState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
